import fetch from 'node-fetch'
import { isDevelopment } from '../../../config/env-checks'
import { setData, getData } from './cache'
import { IGlideFetch, IRequestOptions } from './types'
import { setPageSize, getOptions, getFullPath, isValidResponse } from './utils'

export const glideFetch = async (
  path: string,
  query: unknown = '',
  autoPaginate = true
): Promise<IGlideFetch> => {
  if (!path) throw new Error('Path not provided for glide fetch.')

  const { connectApiUrl, options } = getOptions()
  const queryWithPageSize = setPageSize(query)
  const { page_size: pageSize } = queryWithPageSize
  const fullPath = getFullPath(connectApiUrl, path, queryWithPageSize)
  const cacheKey = `${fullPath}:${autoPaginate}`

  const cachedData = getData(cacheKey)
  if (cachedData) {
    return cachedData as IGlideFetch
  }

  const response = await getResponse(fullPath, options)
  const shouldPaginate = autoPaginate && response.total > pageSize

  if (shouldPaginate) {
    const paginatedData = []
    const maxPages = Math.ceil(response.total / pageSize)

    for (let page = 1; page < maxPages; page++) {
      const fullPath = getFullPath(connectApiUrl, path, queryWithPageSize, page)
      const pagedResponse = await getResponse(fullPath, options)
      paginatedData.push(...pagedResponse.hits)
    }

    const finalResponse = { ...response, hits: [...response.hits, ...paginatedData] }
    setData(cacheKey, finalResponse)
    return finalResponse
  }

  setData(cacheKey, response)
  return response
}

const getResponse = (fullPath: string, options: IRequestOptions): Promise<IGlideFetch> => {
  const maxRetries = 3

  return new Promise((resolve) => {
    const getResponseWithRetries = async (retryAttempt = 0): Promise<void> => {
      if (isDevelopment)
        retryAttempt === 0
          ? console.log('GLIDE FETCH:', fullPath)
          : console.error(`GLIDE FETCH (RETRY ${retryAttempt} OF MAX ${maxRetries})`, fullPath)

      const response = await fetch(fullPath, options)
      const isValid = isValidResponse(response)
      const isRetryLimit = retryAttempt === maxRetries

      // Ensure the response is valid or we've hit the max retry limit. If we haven't then
      // we'll recurse until we hit the max retry limit or the response becomes valid.
      return void (isValid || isRetryLimit
        ? resolve(response.json())
        : getResponseWithRetries(retryAttempt + 1))
    }

    return void getResponseWithRetries()
  })
}
